import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import type { ComponentType, CSSProperties } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/** O'zgarishning ma'nosi: yaxshi tomonga, yomon tomonga yoki betaraf. */
export type StatDeltaTone = "positive" | "negative" | "neutral";

/**
 * Ikonkaning urg'u rangi.
 *
 * Rang MA'NO TASHIMAYDI — u faqat bo'limni ajratadi va uzun kartochkalar
 * qatorida ko'z ilashadigan nuqta beradi. Shuning uchun ranglar
 * `--chart-*` dan alohida: grafikdagi rang qaysi qator ekanini aytadi,
 * bu yerdagisi esa hech narsa aytmaydi va uni "o'qishga" majbur qilib
 * bo'lmaydi.
 */
export type StatAccent = "caramel" | "green" | "teal" | "blue" | "violet" | "rose";

interface StatCardProps {
  label: string;
  value: string;
  /**
   * Ikonka — lucide belgisi yoki loyihaning o'z belgisi (masalan
   * `CoffeeCoin`). Shart faqat bitta: `className` ni qabul qilsin.
   */
  icon?: ComponentType<{ className?: string }>;
  /** Maqsad yoki izoh — raqamning o'zi kontekstsiz hech narsa demaydi. */
  hint?: string;
  /**
   * Ko'rsatkich yomon tomonga ketganini bildiradi. Rang YAGONA belgi
   * emas: `hint` matni ham sababni yozadi, aks holda rang ko'rmaydigan
   * foydalanuvchi ogohlantirishni umuman sezmaydi.
   */
  tone?: "default" | "warning" | "danger";
  accent?: StatAccent;
  /**
   * Oldingi davrga nisbatan o'zgarish, tayyor matn ("+12%").
   *
   * Rang bilan birga ISHORA ham beriladi — matnda plyus yoki minus, va
   * yonida strelka — va `deltaLabel` nimaga nisbatan ekanini aytadi:
   * "+12%" o'zi hech nima demaydi.
   */
  delta?: string;
  deltaTone?: StatDeltaTone;
  deltaLabel?: string;
}

/** Urg'u → pastel fon. Ikki urg'u bitta fonga tushishi mumkin — fon bezak. */
const TILE_BG: Record<StatAccent, string> = {
  caramel: "bg-tile-latte",
  green: "bg-tile-pistachio",
  teal: "bg-tile-sky",
  blue: "bg-tile-sky",
  violet: "bg-tile-berry",
  rose: "bg-tile-berry",
};

const DELTA_ICON = {
  positive: ArrowUpRight,
  negative: ArrowDownRight,
  neutral: Minus,
} as const;

export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  tone = "default",
  accent = "caramel",
  delta,
  deltaTone = "neutral",
  deltaLabel,
}: StatCardProps) {
  const metric = `var(--metric-${accent})`;
  const DeltaIcon = DELTA_ICON[deltaTone];

  return (
    <Card
      // Pastel plita (aimly-admin naqshi): fon rangi faqat bo'limni
      // ajratadi — ma'no (ogohlantirish, o'zgarish) matn va belgida qoladi.
      // `--card-spacing` pasaytirilgan: qator zich turadi, bitta ekranda
      // ko'proq ko'rsatkich sig'adi.
      className={cn(
        "group ring-foreground/5 gap-0 rounded-3xl shadow-none ring-1 transition-transform duration-200 [--card-spacing:--spacing(5)] hover:-translate-y-0.5",
        TILE_BG[accent],
      )}
      style={{ "--metric": metric } as CSSProperties}
    >
      <CardContent className="flex items-start gap-3.5">
        {Icon && (
          // `aria-hidden` va o'lcham O'RAMDA, ikonkaning o'zida emas:
          // `icon` shartnomasi faqat `className` ni va'da qiladi
          // (`CoffeeCoin` boshqa hech narsani qabul qilmaydi).
          <span
            aria-hidden
            className="bg-card grid size-11 shrink-0 place-items-center rounded-2xl shadow-sm transition-transform duration-200 group-hover:-rotate-6"
            style={{ color: "var(--metric)" }}
          >
            <Icon className="size-5" />
          </span>
        )}

        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-foreground/65 truncate text-xs font-medium">{label}</p>

          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <p
              className={cn(
                "text-2xl leading-tight font-semibold tabular-nums",
                tone === "warning" && "text-warning",
                tone === "danger" && "text-destructive",
              )}
            >
              {value}
            </p>

            {delta && (
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums",
                  deltaTone === "positive" && "bg-success/12 text-success",
                  deltaTone === "negative" && "bg-destructive/12 text-destructive",
                  deltaTone === "neutral" && "bg-muted text-muted-foreground",
                )}
                title={deltaLabel}
              >
                <DeltaIcon className="size-3" aria-hidden />
                <span>{delta}</span>
              </span>
            )}
          </div>

          {hint && <p className="text-foreground/55 text-xs">{hint}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
